import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import ShowcaseGrid from "@/components/landing/ShowcaseGrid";

const EXPERIENCES = [
  {
    year: "2026 — Present",
    active: true,
    role: "Software Engineer",
    org: "HypaJump AI",
    sub: "hypajump.ai · Working toward CTO",
    summary:
      "Scope new client work for feasibility, plan delivery, and ship it end-to-end. Authored two internal pipelines — a canonical FastAPI + React + Clerk full-stack boilerplate, and a six-stage project-process template — that compress the path from discovery to handover.",
    tag: "Engineering",
    href: "https://hypajump.ai",
  },
  {
    year: "2026 — Present",
    active: true,
    role: "Research Assistant · Radiology",
    org: "PKU Muhammadiyah Gamping",
    sub: "Faculty of Medicine, Universitas Muhammadiyah Yogyakarta",
    summary:
      "Retrospective study correlating chest imaging patterns with bacterial culture results in pneumonia patients — the kind of evidence that lets clinicians pick smarter empirical antibiotics before cultures come back. Distilled 277 EMRs and 820 inpatient episodes into an analysis-ready cohort of 93 patients, normalising 150+ fields across radiology, microbiology, antibiotic regimens, and CURB-65 severity.",
    tag: "Research",
  },
  {
    year: "May — Jun 2026",
    role: "Publication & Documentation Coordinator",
    org: "CME 62 · UMY",
    sub: "Continuing Medical Education 62nd · Faculty of Medicine, Universitas Muhammadiyah Yogyakarta",
    summary:
      'Led publication and documentation for "Critical Minutes — Mastering Emergency Response in Life-Threatening Conditions", the 62nd CME webinar by FK UMY in collaboration with Eduverse Health.',
    tag: "Organization",
    aspect: "portrait",
    showcase: [
      {
        id: "cme62-1",
        label: "Theme Reveal",
        image: "/assets/experience/cme62-theme.jpg",
      },
      {
        id: "cme62-2",
        label: "Logo Reveal",
        image: "/assets/experience/cme62-logo.jpg",
      },
      {
        id: "cme62-3",
        label: "Webinar Poster",
        image: "/assets/experience/cme62-poster.jpg",
      },
    ],
  },
  {
    year: "2023 — 2024",
    role: "Supervising Council",
    org: "MMSA UMY",
    sub: "Medical and Medical Science Association · Universitas Muhammadiyah Yogyakarta",
    summary:
      "Observed and supervised MMSA officials with focus on the External Team — Advocacy Director and Media Director.",
    tag: "Organization",
    showcase: null,
  },
  {
    year: "2023 — 2024",
    role: "Media & Communication Coordinator",
    org: "SCORE — CIMSA",
    sub: "Standing Committee on Research Exchange · Center for Indonesian Medical Students' Activities",
    summary:
      "Led media and communication initiatives across national research-exchange programmes.",
    tag: "Organization",
    showcase: [
      {
        id: "score-1",
        label: "SCORE-Sight #1",
        image: "/assets/experience/score-sight.jpg",
      },
      {
        id: "score-2",
        label: "Jiankui's Experiment",
        image: "/assets/experience/score-jiankui.jpg",
      },
      {
        id: "score-3",
        label: "Explore Countries #2",
        image: "/assets/experience/score-explore.png",
      },
      {
        id: "score-4",
        label: "Managing Your Financials",
        image: "/assets/experience/score-financials.jpg",
      },
      {
        id: "score-5",
        label: "Raboan Recap",
        image: "/assets/experience/score-raboan.jpg",
      },
    ],
  },
  {
    year: "2022 — 2023",
    role: "Media Director",
    org: "MMSA UMY",
    sub: "Medical and Medical Science Association · Universitas Muhammadiyah Yogyakarta",
    summary:
      "Managed publications, identity, and storytelling output for MMSA UMY.",
    tag: "Organization",
    showcase: [
      {
        id: "mmsa-1",
        label: "Article of SCO · Stunting",
        image: "/assets/experience/mmsa-stunting.jpg",
      },
      {
        id: "mmsa-2",
        label: "Article of SCO · COVID-19",
        image: "/assets/experience/mmsa-covid.jpg",
      },
      {
        id: "mmsa-3",
        label: "Mythbuster · Smoking",
        image: "/assets/experience/mmsa-smoking.jpg",
      },
      {
        id: "mmsa-4",
        label: "Mythbuster #2",
        image: "/assets/experience/mmsa-mythbuster2.jpg",
      },
      {
        id: "mmsa-5",
        label: "Congrats TNT Region 5",
        image: "/assets/experience/mmsa-congrats.jpg",
      },
    ],
  },
  {
    year: "2018 — 2020",
    role: "Freelance Web Developer",
    org: "Independent",
    sub: "Educational institutions · West Sumatra & Bengkulu",
    summary:
      "Built and maintained web-based systems for schools and academic offices across two provinces.",
    tag: "Engineering",
    showcase: null,
  },
];

export default function Experience() {
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="relative py-24 md:py-32 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C09D64]">
              (02) — Track Record
            </p>
          </div>
          <div className="lg:col-span-8">
            <h2
              data-testid="experience-heading"
              className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-white text-balance"
            >
              A path stitched between{" "}
              <span className="italic text-zinc-300">medicine</span>,{" "}
              <span className="italic text-zinc-300">media</span>, and{" "}
              <span className="italic text-zinc-300">making</span>.
            </h2>
          </div>
        </div>

        {/* Timeline list */}
        <ul className="border-t border-white/[0.08]">
          {EXPERIENCES.map((exp, i) => (
            <motion.li
              key={exp.role + exp.year}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              data-testid={`experience-item-${i}`}
              className="group border-b border-white/[0.08]"
            >
              <div className="py-8 md:py-10 transition-colors duration-500 group-hover:bg-white/[0.015]">
                <div className="grid grid-cols-12 gap-4 md:gap-8">
                  {/* Year */}
                  <div className="col-span-12 md:col-span-2">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#C09D64] transition-colors duration-500">
                      {exp.active && (
                        <span
                          aria-hidden
                          data-testid={`experience-item-${i}-active-dot`}
                          className="relative inline-flex w-1.5 h-1.5"
                        >
                          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                          <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        </span>
                      )}
                      {exp.year}
                    </span>
                  </div>

                  {/* Role + Org */}
                  <div className="col-span-12 md:col-span-7">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight transition-transform duration-500 group-hover:translate-x-1">
                        {exp.role}
                      </h3>
                      {exp.href ? (
                        <a
                          href={exp.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`experience-item-${i}-org-link`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-zinc-500 hover:text-[#C09D64] text-sm transition-colors duration-300 inline-flex items-center gap-1"
                        >
                          — {exp.org}
                          <ArrowUpRight className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-zinc-500 text-sm">
                          — {exp.org}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
                      {exp.sub}
                    </p>
                    <p className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
                      {exp.summary}
                    </p>
                  </div>

                  {/* Tag + arrow */}
                  <div className="col-span-12 md:col-span-3 flex md:justify-end items-start">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] text-zinc-300 border border-white/15 rounded-full px-3 py-1">
                        {exp.tag}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500" />
                    </div>
                  </div>
                </div>

                {/* Showcase row — sits below and spans the full content
                    width so tiles can breathe at a generous size */}
                {exp.showcase && (
                  <div className="mt-4">
                    <ShowcaseGrid
                      parentTestId={`experience-item-${i}-showcase`}
                      parentRole={`${exp.role} · ${exp.org}`}
                      items={exp.showcase}
                      aspect={exp.aspect || "square"}
                    />
                  </div>
                )}
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
